import { normalizeCustomerId } from '@/pages/GoogleAdsAccountSelection/googleAdsAccountSelectionModel';
import type { BrandSelectOption } from '../BrandSelectMenu';

interface GoogleAdsBusinessGroupRecord extends BrandSelectOption {
    visibility?: 'WORKSPACE' | 'PRIVATE';
}

interface GoogleAdsAssignedResourceRecord {
    resourceType: string;
    resourceKey: string;
    brandIds: string[];
    brandAccessCeilings?: Record<string, 'READ' | 'WRITE' | 'DISABLED'>;
}

interface GoogleAdsConnectionRecord {
    id: string;
    provider: string;
    accountDetail: string;
    resources: GoogleAdsAssignedResourceRecord[];
}

interface GoogleAdsWorkspaceIntegrationMappingRecord {
    id?: string | number | null;
    slugId?: string | null;
    slug_id?: string | null;
    connection_id?: string | null;
    connectionId?: string | null;
    nango_connection_id?: string | null;
    nangoConnectionId?: string | null;
    external_account_id?: string | null;
    externalAccountId?: string | null;
}

interface GoogleAdsWorkspaceIntegrationRecord
    extends GoogleAdsWorkspaceIntegrationMappingRecord {
    provider: string;
    mappings?: GoogleAdsWorkspaceIntegrationMappingRecord[];
}

const normalizeProvider = (value: string) =>
    value.trim().toLowerCase().replace(/[\s_]+/g, '-');

const normalizeConnectionIdentity = (value: unknown) =>
    String(value ?? '').trim().toLowerCase();

const getConnectionIdentities = (
    record: GoogleAdsWorkspaceIntegrationMappingRecord,
) => [
    record.id,
    record.slugId,
    record.slug_id,
    record.connection_id,
    record.connectionId,
    record.nango_connection_id,
    record.nangoConnectionId,
    record.external_account_id,
    record.externalAccountId,
];

const hasConnectionIdentity = (
    record: GoogleAdsWorkspaceIntegrationMappingRecord,
    target: string,
) => getConnectionIdentities(record).some(
    (identity) => normalizeConnectionIdentity(identity) === target,
);

export const getGoogleAdsConnectionIdentities = (
    integrations: GoogleAdsWorkspaceIntegrationRecord[],
    connectionIdentity?: string | null,
): string[] => {
    const target = normalizeConnectionIdentity(connectionIdentity);
    if (!target) return [];

    const identities = new Set<string>([String(connectionIdentity).trim()]);
    integrations
        .filter(
            (integration) =>
                normalizeProvider(integration.provider) === 'google-ads',
        )
        .forEach((integration) => {
            const integrationIdentities = getConnectionIdentities(integration);
            if (hasConnectionIdentity(integration, target)) {
                integrationIdentities.forEach((identity) => {
                    const value = String(identity ?? '').trim();
                    if (value) identities.add(value);
                });
            }

            (integration.mappings || [])
                .filter((mapping) => hasConnectionIdentity(mapping, target))
                .forEach((mapping) => {
                    getConnectionIdentities(mapping).forEach((identity) => {
                        const value = String(identity ?? '').trim();
                        if (value) identities.add(value);
                    });
                });
        });

    return [...identities];
};

export const getGoogleAdsPublicBusinessGroupOptions = (
    businessGroups: GoogleAdsBusinessGroupRecord[],
): BrandSelectOption[] => businessGroups
    .filter((businessGroup) => businessGroup.visibility === 'WORKSPACE')
    .map(({
        id, name, logoUrl, accent, isDefault, creatorName,
    }) => ({
        id,
        name,
        logoUrl,
        accent,
        isDefault,
        creatorName,
    }));

export const getGoogleAdsAssignedBusinessGroupIdsByCustomerId = (
    connections: GoogleAdsConnectionRecord[],
    connectionIdentity?: string | readonly string[] | null,
): Record<string, string[]> => {
    const normalizedConnectionIdentities = new Set(
        (typeof connectionIdentity === 'string'
            ? [connectionIdentity]
            : connectionIdentity || []
        )
            .map(normalizeConnectionIdentity)
            .filter(Boolean),
    );
    if (normalizedConnectionIdentities.size === 0) return {};

    const assignedBrandIds = new Map<string, Set<string>>();
    connections
        .filter(
            (connection) =>
                normalizeProvider(connection.provider) === 'google-ads'
                && [connection.id, connection.accountDetail].some(
                    (identity) =>
                        normalizedConnectionIdentities.has(
                            normalizeConnectionIdentity(identity),
                        ),
                ),
        )
        .flatMap((connection) => connection.resources || [])
        .filter(
            (resource) =>
                resource.resourceType.trim().toUpperCase() === 'CUSTOMER',
        )
        .forEach((resource) => {
            const customerId = normalizeCustomerId(resource.resourceKey);
            if (!customerId) return;
            const current = assignedBrandIds.get(customerId) || new Set<string>();
            resource.brandIds.forEach((brandId) => current.add(brandId));
            assignedBrandIds.set(customerId, current);
        });

    return Object.fromEntries(
        [...assignedBrandIds].map(([customerId, brandIds]) => [
            customerId,
            [...brandIds],
        ]),
    );
};

const mergeBusinessGroupAccessLevel = (
    current: 'READ' | 'WRITE' | undefined,
    next: 'READ' | 'WRITE' | undefined,
): 'READ' | 'WRITE' | undefined => {
    if (!next) return current;
    if (!current) return next;
    return current === 'WRITE' || next === 'WRITE' ? 'WRITE' : 'READ';
};

export const getGoogleAdsAssignedBusinessGroupAccessLevelsByCustomerId = (
    connections: GoogleAdsConnectionRecord[],
    connectionIdentity?: string | readonly string[] | null,
): Record<string, Record<string, 'READ' | 'WRITE'>> => {
    const normalizedConnectionIdentities = new Set(
        (typeof connectionIdentity === 'string'
            ? [connectionIdentity]
            : connectionIdentity || []
        )
            .map(normalizeConnectionIdentity)
            .filter(Boolean),
    );
    if (normalizedConnectionIdentities.size === 0) return {};

    const assignedAccessLevels = new Map<string, Record<string, 'READ' | 'WRITE'>>();
    connections
        .filter(
            (connection) =>
                normalizeProvider(connection.provider) === 'google-ads'
                && [connection.id, connection.accountDetail].some(
                    (identity) =>
                        normalizedConnectionIdentities.has(
                            normalizeConnectionIdentity(identity),
                        ),
                ),
        )
        .flatMap((connection) => connection.resources || [])
        .filter(
            (resource) =>
                resource.resourceType.trim().toUpperCase() === 'CUSTOMER',
        )
        .forEach((resource) => {
            const customerId = normalizeCustomerId(resource.resourceKey);
            if (!customerId) return;

            const current = assignedAccessLevels.get(customerId) || {};
            const nextLevels = resource.brandAccessCeilings || {};
            resource.brandIds.forEach((brandId) => {
                const ceiling = nextLevels[brandId];
                const merged = mergeBusinessGroupAccessLevel(current[brandId], ceiling === 'READ' || ceiling === 'WRITE' ? ceiling : undefined);
                if (merged) {
                    current[brandId] = merged;
                }
            });
            assignedAccessLevels.set(customerId, current);
        });

    return Object.fromEntries(
        [...assignedAccessLevels].map(([customerId, accessLevels]) => [
            customerId,
            accessLevels,
        ]),
    );
};

export const getAssignableGoogleAdsBusinessGroups = (
    publicBusinessGroups: BrandSelectOption[],
    assignedBusinessGroupIdsByCustomerId: Readonly<Record<string, string[]>>,
    customerId?: string | null,
): BrandSelectOption[] => {
    const normalizedCustomerId = normalizeCustomerId(customerId || '');
    const assignedIds = new Set(
        assignedBusinessGroupIdsByCustomerId[normalizedCustomerId] || [],
    );
    return publicBusinessGroups.filter(
        (businessGroup) => !assignedIds.has(businessGroup.id),
    );
};
